/**
 * STAKE FORM
 * 
 * Formulário para editar percentual de participação no investimento
 */

import { Paper, Typography, TextField, Box, InputAdornment, Alert } from '@mui/material';
import { Percent } from '@mui/icons-material';

export interface StakeFormData {
  percentual: number;
  descricao?: string;
}

interface StakeFormProps {
  values: StakeFormData;
  onChange: (values: StakeFormData) => void;
}

export function StakeForm({ values, onChange }: StakeFormProps) {
  const handlePercentualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = parseFloat(e.target.value) || 0;
    // Limitar entre 0 e 100
    const valorLimitado = Math.max(0, Math.min(100, valor));
    onChange({
      ...values,
      percentual: valorLimitado / 100
    });
  };

  const handleDescricaoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...values,
      descricao: e.target.value
    });
  };

  // Validação
  const percentualExibido = (values.percentual * 100).toFixed(2);
  const isValid = values.percentual > 0 && values.percentual <= 1;

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Percent />
        Participação no Investimento
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define o percentual de participação a ser calculado sobre o Equity Ajustado (pós-descontos).
        Este valor será aplicado nas projeções de ambas empresas (Sétima + 555 Studios).
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Percentual de Participação"
          type="number"
          value={percentualExibido}
          onChange={handlePercentualChange}
          inputProps={{
            min: 0,
            max: 100,
            step: 0.1
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>
          }}
          sx={{ flex: '1 1 200px', minWidth: '200px' }}
          error={!isValid}
          helperText={!isValid ? 'Percentual deve estar entre 0% e 100%' : ''}
        />

        <TextField
          label="Descrição (opcional)"
          value={values.descricao || ''}
          onChange={handleDescricaoChange}
          placeholder="Ex: Participação conforme Term Sheet"
          sx={{ flex: '2 1 300px', minWidth: '300px' }}
        />
      </Box>

      {!isValid && (
        <Alert severity="error" sx={{ mt: 2 }}>
          O percentual de participação deve ser maior que 0% e no máximo 100%.
        </Alert>
      )}

      {isValid && values.percentual > 0.5 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Participação superior a 50% - verifique se está correto conforme Term Sheet.
        </Alert>
      )}
    </Paper>
  );
}
